import React, { useState, useEffect } from 'react';
import { planAPI } from '../services/api';
import { Plan } from '../types';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Collapse,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Edit, Delete, Refresh, FilterList, Clear, Add, Science } from '@mui/icons-material';

// Define proper error type
interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

// Define dummy plan template type
interface DummyPlanTemplate {
  name: string;
  description: string;
  price: number;
  billingPeriod: string;
  durationInMonths: number;
  features: string[];
}

const Plans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterForm, setFilterForm] = useState({
    planId: '',
    name: '',
    billingPeriod: '',
    isPopular: '',
    isActive: ''
  });
  
  const [form, setForm] = useState({
    planId: '',
    name: '',
    description: '',
    price: '',
    currency: 'INR',
    billingPeriod: 'monthly',
    durationInMonths: '',
    monthlyEquivalent: '',
    savingsPercentage: '',
    features: '',
    isPopular: false,
    isActive: true
  });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [showDummyPlanModal, setShowDummyPlanModal] = useState(false);
  const [creatingDummyPlan, setCreatingDummyPlan] = useState(false);

  const billingPeriods = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: '3 Months' },
    { value: 'yearly', label: '1 Year' },
  ];

  const currencies = [
    { value: 'INR', label: 'Indian Rupee (₹)' },
    { value: 'USD', label: 'US Dollar ($)' },
    { value: 'EUR', label: 'Euro (€)' },
  ];

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await planAPI.getAllPlans();
      setPlans(res.data);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = async () => {
    setFilterLoading(true);
    setError(null);
    try {
      const res = await planAPI.getAllPlans();
      let filteredPlans = res.data;
      
      if (filterForm.planId) {
        filteredPlans = filteredPlans.filter(plan => 
          plan.planId.toLowerCase().includes(filterForm.planId.toLowerCase())
        );
      }
      
      if (filterForm.name) {
        filteredPlans = filteredPlans.filter(plan => 
          plan.name.toLowerCase().includes(filterForm.name.toLowerCase())
        );
      }
      
      if (filterForm.billingPeriod) {
        filteredPlans = filteredPlans.filter(plan => 
          plan.billingPeriod === filterForm.billingPeriod
        );
      }
      
      if (filterForm.isPopular !== '') {
        filteredPlans = filteredPlans.filter(plan => 
          plan.isPopular === (filterForm.isPopular === 'true')
        );
      }
      
      if (filterForm.isActive !== '') {
        filteredPlans = filteredPlans.filter(plan => 
          plan.isActive === (filterForm.isActive === 'true')
        );
      }
      
      setPlans(filteredPlans);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to apply filters');
    } finally {
      setFilterLoading(false);
    }
  };

  const clearFilters = () => {
    setFilterForm({
      planId: '',
      name: '',
      billingPeriod: '',
      isPopular: '',
      isActive: ''
    });
    fetchPlans();
  };

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>) => {
    setFilterForm({
      ...filterForm,
      [e.target.name]: e.target.value
    });
  };

  const openAddModal = async () => {
    setForm({
      planId: '',
      name: '',
      description: '',
      price: '',
      currency: 'INR',
      billingPeriod: 'monthly',
      durationInMonths: '',
      monthlyEquivalent: '',
      savingsPercentage: '',
      features: '',
      isPopular: false,
      isActive: true
    });
    setFormError('');
    setIsEditMode(false);
    setEditingPlanId(null);
    setShowModal(true);
  };

  const openEditModal = async (plan: Plan) => {
    setForm({
      planId: plan.planId,
      name: plan.name,
      description: plan.description || '',
      price: plan.price.toString(),
      currency: plan.currency,
      billingPeriod: plan.billingPeriod,
      durationInMonths: plan.durationInMonths.toString(),
      monthlyEquivalent: plan.monthlyEquivalent?.toString() || '',
      savingsPercentage: plan.savingsPercentage?.toString() || '',
      features: plan.features?.join(', ') || '',
      isPopular: plan.isPopular || false,
      isActive: plan.isActive || false
    });
    setFormError('');
    setIsEditMode(true);
    setEditingPlanId(plan.id!);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setFormError('');
  };

  const handleTextFieldInput = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleSelectInput = (e: SelectChangeEvent<string>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value
    });
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({
      ...form,
      [e.target.name]: e.target.checked
    });
  };

  const handleSavePlan = async () => {
    if (!form.planId || !form.name || !form.price || !form.billingPeriod || !form.durationInMonths) {
      setFormError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const planData = {
        ...form,
        price: parseFloat(form.price),
        durationInMonths: parseInt(form.durationInMonths),
        monthlyEquivalent: form.monthlyEquivalent ? parseFloat(form.monthlyEquivalent) : undefined,
        savingsPercentage: form.savingsPercentage ? parseFloat(form.savingsPercentage) : undefined,
        features: form.features ? form.features.split(',').map(f => f.trim()) : []
      };

      if (isEditMode && editingPlanId) {
        await planAPI.updatePlan(editingPlanId, planData);
        setSuccessMsg('Plan updated successfully');
      } else {
        await planAPI.createPlan(planData);
        setSuccessMsg('Plan created successfully');
      }
      await fetchPlans();
      closeModal();
    } catch (e: unknown) {
      const error = e as ApiError;
      setFormError(error.response?.data?.message || error.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;
    
    setDeletingId(id);
    try {
      await planAPI.deletePlan(id);
      setSuccessMsg('Plan deleted successfully');
      await fetchPlans();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to delete plan');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleStatus = async (plan: Plan) => {
    try {
      await planAPI.updatePlan(plan.id!, { 
        planId: plan.planId,
        name: plan.name,
        price: plan.price,
        billingPeriod: plan.billingPeriod,
        durationInMonths: plan.durationInMonths,
        isActive: !plan.isActive 
      });
      setSuccessMsg(`Plan ${plan.isActive ? 'deactivated' : 'activated'} successfully`);
      await fetchPlans();
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to update plan status');
    }
  };

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
    }).format(price);
  };

  // Dummy plan templates
  const dummyPlanTemplates: DummyPlanTemplate[] = [
    {
      name: 'Basic Monthly',
      description: 'Basic monthly subscription for testing',
      price: 99,
      billingPeriod: 'monthly',
      durationInMonths: 1,
      features: ['Live Navigation', 'Station Alerts']
    },
    {
      name: 'Premium Monthly',
      description: 'Premium monthly subscription with all features',
      price: 199,
      billingPeriod: 'monthly',
      durationInMonths: 1,
      features: ['Live Navigation', 'Station Alerts', 'Live Location Sharing', 'Disruption Alerts', 'Multi-Language Support', 'Ad-Free']
    },
    {
      name: 'Premium Quarterly',
      description: 'Premium quarterly subscription with savings',
      price: 499,
      billingPeriod: 'quarterly',
      durationInMonths: 3,
      features: ['Live Navigation', 'Station Alerts', 'Live Location Sharing', 'Disruption Alerts', 'Multi-Language Support', 'Ad-Free']
    },
    {
      name: 'Premium Yearly',
      description: 'Premium yearly subscription with maximum savings',
      price: 1499,
      billingPeriod: 'yearly',
      durationInMonths: 12,
      features: ['Live Navigation', 'Station Alerts', 'Live Location Sharing', 'Disruption Alerts', 'Multi-Language Support', 'Ad-Free']
    },
    {
      name: 'Test Plan (Inactive)',
      description: 'Test plan that is inactive',
      price: 299,
      billingPeriod: 'monthly',
      durationInMonths: 1,
      features: ['Live Navigation', 'Station Alerts']
    }
  ];

  const createDummyPlan = async (template: DummyPlanTemplate) => {
    setCreatingDummyPlan(true);
    try {
      const planData = {
        planId: `PLAN_${Date.now()}`,
        name: template.name,
        description: template.description,
        price: template.price,
        currency: 'INR',
        billingPeriod: template.billingPeriod,
        durationInMonths: template.durationInMonths,
        monthlyEquivalent: template.price,
        savingsPercentage: 0,
        features: template.features,
        isPopular: false,
        isActive: true
      };

      await planAPI.createPlan(planData);
      setSuccessMsg('Dummy plan created successfully');
      await fetchPlans();
      setShowDummyPlanModal(false);
    } catch (e: unknown) {
      const error = e as ApiError;
      setError(error.response?.data?.message || error.message || 'Failed to create dummy plan');
    } finally {
      setCreatingDummyPlan(false);
    }
  };

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Plans Management
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchPlans}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={openAddModal}
            sx={{ borderRadius: 2 }}
          >
            Add Plan
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<Science />}
            onClick={() => setShowDummyPlanModal(true)}
          >
            Create Dummy Plan
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filter Plans
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Plan ID"
                  name="planId"
                  value={filterForm.planId}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Plan Name"
                  name="name"
                  value={filterForm.name}
                  onChange={handleFilterChange}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Billing Period</InputLabel>
                  <Select
                    name="billingPeriod"
                    value={filterForm.billingPeriod}
                    onChange={handleFilterChange}
                    label="Billing Period"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                    <MenuItem value="quarterly">3 Months</MenuItem>
                    <MenuItem value="yearly">1 Year</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Popular</InputLabel>
                  <Select
                    name="isPopular"
                    value={filterForm.isPopular}
                    onChange={handleFilterChange}
                    label="Popular"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Active</InputLabel>
                  <Select
                    name="isActive"
                    value={filterForm.isActive}
                    onChange={handleFilterChange}
                    label="Active"
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box display="flex" gap={1}>
                  <Button
                    variant="contained"
                    onClick={applyFilters}
                    disabled={filterLoading}
                    size="small"
                  >
                    {filterLoading ? <CircularProgress size={16} /> : 'Apply'}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={clearFilters}
                    size="small"
                    startIcon={<Clear />}
                  >
                    Clear
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Collapse>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Plan ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Popular</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">No plans found.</TableCell>
                  </TableRow>
                ) : plans.map(plan => (
                  <TableRow key={plan.id} hover>
                    <TableCell>
                      <Chip label={plan.planId} size="small" color="primary" />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {plan.name}
                        </Typography>
                        {plan.description && (
                          <Typography variant="caption" color="text.secondary">
                            {plan.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {formatPrice(plan.price, plan.currency)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {plan.billingPeriod}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={`${plan.durationInMonths} months`} 
                        size="small" 
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={plan.isPopular ? 'Yes' : 'No'} 
                        color={plan.isPopular ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={plan.isActive ? 'Yes' : 'No'} 
                        color={plan.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {plan.features?.slice(0, 2).map((feature, index) => (
                          <Chip
                            key={index}
                            label={feature}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        ))}
                        {plan.features && plan.features.length > 2 && (
                          <Chip
                            label={`+${plan.features.length - 2}`}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEditModal(plan)}
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(plan.id!)}
                            disabled={deletingId === plan.id}
                          >
                            {deletingId === plan.id ? <CircularProgress size={16} /> : <Delete />}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog open={showModal} onClose={closeModal} maxWidth="md" fullWidth>
        <DialogTitle>{isEditMode ? 'Edit Plan' : 'Add New Plan'}</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan ID"
                name="planId"
                value={form.planId}
                onChange={handleTextFieldInput}
                helperText="Unique identifier (e.g., monthly, quarterly, yearly)"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Plan Name"
                name="name"
                value={form.name}
                onChange={handleTextFieldInput}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                name="description"
                value={form.description}
                onChange={handleTextFieldInput}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Price"
                name="price"
                type="number"
                value={form.price}
                onChange={handleTextFieldInput}
                required
                InputProps={{
                  startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>₹</Typography>,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select
                  name="currency"
                  value={form.currency}
                  onChange={handleSelectInput}
                  label="Currency"
                >
                  {currencies.map((currency) => (
                    <MenuItem key={currency.value} value={currency.value}>
                      {currency.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Billing Period</InputLabel>
                <Select
                  name="billingPeriod"
                  value={form.billingPeriod}
                  onChange={handleSelectInput}
                  label="Billing Period"
                >
                  {billingPeriods.map((period) => (
                    <MenuItem key={period.value} value={period.value}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Duration (Months)"
                name="durationInMonths"
                type="number"
                value={form.durationInMonths}
                onChange={handleTextFieldInput}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monthly Equivalent"
                name="monthlyEquivalent"
                type="number"
                value={form.monthlyEquivalent}
                onChange={handleTextFieldInput}
                helperText="Monthly equivalent price"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Savings Percentage"
                name="savingsPercentage"
                type="number"
                value={form.savingsPercentage}
                onChange={handleTextFieldInput}
                helperText="Savings percentage compared to monthly"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Features (comma-separated)"
                name="features"
                value={form.features}
                onChange={handleTextFieldInput}
                multiline
                rows={3}
                helperText="Enter features separated by commas"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isPopular"
                    checked={form.isPopular}
                    onChange={handleCheckbox}
                  />
                }
                label="Popular Plan"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isActive"
                    checked={form.isActive}
                    onChange={handleCheckbox}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePlan} 
            variant="contained" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={16} /> : (isEditMode ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dummy Plan Creation Modal */}
      <Dialog open={showDummyPlanModal} onClose={() => setShowDummyPlanModal(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Science color="secondary" />
            Create Dummy Plans for Testing
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Quickly create test plans to verify subscription functionality. These plans are designed for testing purposes.
          </Typography>
          <Grid container spacing={2}>
            {dummyPlanTemplates.map((template, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { 
                      boxShadow: 3,
                      border: '1px solid',
                      borderColor: 'primary.main'
                    }
                  }}
                  onClick={() => createDummyPlan(template)}
                >
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Typography variant="h6" fontWeight="bold">
                        {template.name}
                      </Typography>
                      <Chip 
                        label="Active" 
                        size="small" 
                        color="success"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6" color="primary">
                        ₹{template.price}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {template.billingPeriod} • {template.durationInMonths} month{template.durationInMonths > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Features: {template.features.slice(0, 2).join(', ')}
                      {template.features.length > 2 && ` +${template.features.length - 2} more`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDummyPlanModal(false)}>
            Cancel
          </Button>
          {creatingDummyPlan && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <Typography variant="body2">Creating...</Typography>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Plans; 